import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';

import { formatDateTime } from '../../utils/DiactiveUtils';

const compareTimestamps = (a, b) => {
  const aMs = a?.toDate ? a.toDate().getTime() : 0;
  const bMs = b?.toDate ? b.toDate().getTime() : 0;
  return aMs - bMs;
};

const compareStrings = (a, b) =>
  (a ?? '').localeCompare(b ?? '', 'es', { sensitivity: 'base' });

const columns = [
  {
    key: 'createdAt',
    header: 'Fecha de alta',
    width: 200,
    defaultDir: 'desc',
    sort: (a, b) => compareTimestamps(a.createdAt, b.createdAt),
    render: (h) => formatDateTime(h.createdAt),
  },
  {
    key: 'id',
    header: 'ID',
    width: 220,
    sort: (a, b) => compareStrings(a.id, b.id),
    render: (h) => h.id,
    tooltip: (h) => h.id,
  },
  {
    key: 'name',
    header: 'Nombre',
    width: 360,
    sort: (a, b) => compareStrings(a.name, b.name),
    render: (h) => h.name || '—',
    tooltip: (h) => h.name || '',
  },
  {
    key: 'shortName',
    header: 'Nombre corto',
    width: 220,
    sort: (a, b) => compareStrings(a.shortName, b.shortName),
    render: (h) => h.shortName || '—',
    tooltip: (h) => h.shortName || '',
  },
];

const HospitalsTable = ({ items }) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const column = columns.find((c) => c.key === sortConfig.key);
  const direction = sortConfig.direction === 'asc' ? 1 : -1;
  const sorted = column?.sort
    ? [...items].sort((a, b) => column.sort(a, b) * direction)
    : items;

  const handleSort = (key) => {
    const col = columns.find((c) => c.key === key);
    const defaultDir = col?.defaultDir ?? 'asc';
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key
          ? prev.direction === 'asc'
            ? 'desc'
            : 'asc'
          : defaultDir,
    }));
  };

  const onRowClick = (id) => {
    navigate(`/hospital/view/${id}`);
  };

  const renderCell = (col, item) => {
    const content = col.render(item);
    if (!col.tooltip) return content;
    const tooltipText = col.tooltip(item);
    return (
      <Tooltip title={tooltipText} disableHoverListener={!tooltipText}>
        <Typography
          variant="body2"
          sx={{
            maxWidth: col.width,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {content}
        </Typography>
      </Tooltip>
    );
  };

  return (
    <Box>
      <TableContainer>
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ width: col.width, maxWidth: col.width }}>
                  <TableSortLabel
                    active={sortConfig.key === col.key}
                    direction={sortConfig.direction}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.header}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((item) => (
              <TableRow
                key={item.id}
                hover
                onClick={() => onRowClick(item.id)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                {columns.map((col) => (
                  <TableCell key={col.key} sx={{ width: col.width, maxWidth: col.width }}>
                    {renderCell(col, item)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No hay hospitales
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default HospitalsTable;
